import React, { useMemo, useState, useEffect } from 'react';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';
import api from '../utils/api';
import { useAuth } from '../utils/AuthContext';
import ConfirmDialog from './ConfirmDialog';
import ModalShell from './ModalShell';

function normalizePhone(phoneValue) {
  const raw = String(phoneValue || '').trim().replace(/[\s\-()]/g, '');
  if (raw.startsWith('+')) {
    return `+${raw.slice(1).replace(/\D/g, '')}`;
  }
  if (raw.startsWith('00')) {
    return `+${raw.slice(2).replace(/\D/g, '')}`;
  }
  return raw.replace(/\D/g, '');
}

function validatePhone(phoneValue) {
  const digits = normalizePhone(phoneValue).replace(/\D/g, '');
  if (digits.length < 9) {
    return 'Numer telefonu jest za krótki.';
  }
  if (digits.length > 15) {
    return 'Numer telefonu jest za długi.';
  }
  return '';
}

export default function AddAppointmentModal({ onClose, onSaved, defaultDate }) {
  const { professional } = useAuth();
  const [services, setServices] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedAddonIds, setSelectedAddonIds] = useState([]);
  const [form, setForm] = useState({
    client_name: '',
    client_phone: '',
    service_id: '',
    scheduled_at: '',
    notes: '',
  });
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({
    client_name: '',
    client_phone: '',
    service_id: '',
    scheduled_at: '',
  });
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get('/services/mine'),
      api.get('/clients'),
    ])
      .then(([servicesResponse, clientsResponse]) => {
        setServices(servicesResponse.data.filter((s) => s.is_active));
        setClients(Array.isArray(clientsResponse.data) ? clientsResponse.data : []);
        setError('');
      })
      .catch(() => {
        setError('Nie udało się pobrać danych do formularza.');
      });
  }, []);

  useEffect(() => {
    let mounted = true;

    async function fetchSlots() {
      if (!form.service_id || !professional?.id) {
        setSlots([]);
        return;
      }

      setSlotsLoading(true);
      try {
        const startDate = format(defaultDate || new Date(), 'yyyy-MM-dd');
        const { data } = await api.get(
          `/availability/slots?professionalId=${professional.id}&serviceId=${form.service_id}&startDate=${startDate}&days=21&addonIds=${selectedAddonIds.join(',')}`
        );
        if (!mounted) return;
        const normalizedSlots = Array.isArray(data) ? data : Array.isArray(data?.slots) ? data.slots : [];
        setSlots(normalizedSlots);
        setError('');
      } catch (err) {
        if (!mounted) return;
        setError(err.response?.data?.error || 'Nie udało się pobrać wolnych terminów.');
      } finally {
        if (mounted) {
          setSlotsLoading(false);
        }
      }
    }

    fetchSlots();
    return () => {
      mounted = false;
    };
  }, [defaultDate, form.service_id, professional?.id, selectedAddonIds]);

  const selectedService = useMemo(
    () => services.find((service) => String(service.id) === String(form.service_id)) || null,
    [services, form.service_id]
  );

  const groupedSlots = useMemo(() => {
    const groups = {};
    for (const slot of slots) {
      const key = format(new Date(slot.start), 'yyyy-MM-dd');
      if (!groups[key]) groups[key] = [];
      groups[key].push(slot);
    }
    return Object.entries(groups);
  }, [slots]);

  const selectedSlotLabel = useMemo(() => {
    if (!form.scheduled_at) return '';
    return format(new Date(form.scheduled_at), 'EEEE, d MMMM · HH:mm', { locale: pl });
  }, [form.scheduled_at]);

  const selectedAddons = useMemo(
    () => (selectedService?.addons || []).filter((addon) => selectedAddonIds.includes(addon.id)),
    [selectedAddonIds, selectedService]
  );

  const pricingSummary = useMemo(() => {
    if (!selectedService) {
      return { duration: 0, price: 0 };
    }

    const basePrice = Number(selectedService.effective_price ?? selectedService.promo_price ?? selectedService.price ?? 0);
    const addonsPrice = selectedAddons.reduce((sum, addon) => sum + Number(addon.price || 0), 0);
    const addonsDuration = selectedAddons.reduce((sum, addon) => sum + Number(addon.duration_minutes || 0), 0);

    return {
      duration: Number(selectedService.duration_minutes || 0) + addonsDuration,
      price: basePrice + addonsPrice,
    };
  }, [selectedAddons, selectedService]);

  const matchedClients = useMemo(() => {
    const query = form.client_name.trim().toLowerCase();
    if (query.length < 2) return [];
    return clients
      .filter((client) => client.name?.trim().toLowerCase().includes(query))
      .slice(0, 5);
  }, [clients, form.client_name]);

  function findClientByName(value) {
    const exactMatches = clients.filter(
      (client) => client.name?.trim().toLowerCase() === value.trim().toLowerCase()
    );
    return exactMatches.length === 1 ? exactMatches[0] : null;
  }

  function findClientByPhone(value) {
    const normalizedInput = normalizePhone(value);
    return clients.find((client) => normalizePhone(client.phone) === normalizedInput);
  }

  function handleNameChange(value) {
    const matchedClient = findClientByName(value);
    setForm((prev) => ({
      ...prev,
      client_name: value,
      client_phone: matchedClient ? matchedClient.phone : prev.client_phone,
    }));
    setFieldErrors((prev) => ({ ...prev, client_name: '' }));
    setError('');
  }

  function handlePhoneChange(value) {
    const matchedClient = findClientByPhone(value);
    setForm((prev) => ({
      ...prev,
      client_phone: value,
      client_name: matchedClient ? matchedClient.name : prev.client_name,
    }));
    setFieldErrors((prev) => ({ ...prev, client_phone: '' }));
    setError('');
  }

  function validateForm() {
    return {
      client_name: !form.client_name.trim() ? 'Podaj imię klientki.' : '',
      client_phone: !form.client_phone.trim()
        ? 'Podaj numer telefonu.'
        : validatePhone(form.client_phone),
      service_id: !form.service_id ? 'Wybierz usługę.' : '',
      scheduled_at: !form.scheduled_at ? 'Wybierz wolny termin.' : '',
    };
  }

  const hasUnsavedChanges = Boolean(
    form.client_name.trim() ||
    form.client_phone.trim() ||
    form.service_id ||
    form.scheduled_at ||
    form.notes.trim() ||
    selectedAddonIds.length > 0
  );

  function requestClose() {
    if (!hasUnsavedChanges || saving) {
      onClose();
      return;
    }

    setShowCloseConfirm(true);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const nextErrors = validateForm();
    setFieldErrors(nextErrors);
    if (Object.values(nextErrors).some(Boolean)) {
      setError('Popraw zaznaczone pola formularza.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await api.post('/appointments', {
        ...form,
        service_id: parseInt(form.service_id),
        client_phone: normalizePhone(form.client_phone),
        addon_ids: selectedAddonIds,
      });
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || 'Błąd zapisu.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <ModalShell title="Dodaj wizytę" onClose={requestClose}>

        {error && <div className="alert alert-error">{error}</div>}

        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-group">
            <label>Imię klientki *</label>
            <input
              type="text"
              list="client-name-suggestions"
              value={form.client_name}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder="np. Kasia"
              className={fieldErrors.client_name ? 'input-error' : ''}
              aria-invalid={fieldErrors.client_name ? 'true' : 'false'}
            />
            <datalist id="client-name-suggestions">
              {clients.map((client) => (
                <option key={`name-${client.id}`} value={client.name} label={client.phone} />
              ))}
            </datalist>
            <div className="field-hint">Mozesz wybrac istniejaca klientke albo wpisac nowa recznie.</div>
            {fieldErrors.client_name && <div className="field-error">{fieldErrors.client_name}</div>}
            {matchedClients.length > 1 && (
              <div className="client-match-list">
                {matchedClients.map((client) => (
                  <button
                    key={`match-${client.id}`}
                    type="button"
                    className="client-match-btn"
                    onClick={() => {
                      setForm((prev) => ({
                        ...prev,
                        client_name: client.name,
                        client_phone: client.phone,
                      }));
                      setFieldErrors((prev) => ({ ...prev, client_name: '', client_phone: '' }));
                      setError('');
                    }}
                  >
                    <span>{client.name}</span>
                    <span>{client.phone}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="form-group">
            <label>Numer telefonu *</label>
            <input
              type="tel"
              list="client-phone-suggestions"
              value={form.client_phone}
              onChange={(e) => handlePhoneChange(e.target.value)}
              placeholder="600 100 200"
              inputMode="tel"
              maxLength={20}
              className={fieldErrors.client_phone ? 'input-error' : ''}
              aria-invalid={fieldErrors.client_phone ? 'true' : 'false'}
            />
            <datalist id="client-phone-suggestions">
              {clients.map((client) => (
                <option key={`phone-${client.id}`} value={client.phone} label={client.name} />
              ))}
            </datalist>
            {fieldErrors.client_phone && <div className="field-error">{fieldErrors.client_phone}</div>}
          </div>
          <div className="form-group">
            <label>Usługa *</label>
            <select
              value={form.service_id}
              onChange={(e) => {
                setError('');
                setFieldErrors((prev) => ({ ...prev, service_id: '', scheduled_at: '' }));
                setSelectedAddonIds([]);
                setForm({ ...form, service_id: e.target.value, scheduled_at: '' });
              }}
              className={fieldErrors.service_id ? 'input-error' : ''}
              aria-invalid={fieldErrors.service_id ? 'true' : 'false'}
            >
              <option value="">Wybierz usługę...</option>
              {services.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} – {s.duration_minutes} min – {s.price} zł
                </option>
              ))}
            </select>
            {fieldErrors.service_id && <div className="field-error">{fieldErrors.service_id}</div>}
          </div>
          {selectedService && Array.isArray(selectedService.addons) && selectedService.addons.length > 0 && (
            <div className="form-group">
              <label>Dodatki do usługi</label>
              <div className="addon-list">
                {selectedService.addons.map((addon) => {
                  const active = selectedAddonIds.includes(addon.id);
                  return (
                    <button
                      key={addon.id}
                      type="button"
                      className={`addon-chip ${active ? 'addon-chip--selected' : ''}`}
                      onClick={() => {
                        setForm((prev) => ({ ...prev, scheduled_at: '' }));
                        setFieldErrors((prev) => ({ ...prev, scheduled_at: '' }));
                        setSelectedAddonIds((prev) => (
                          prev.includes(addon.id)
                            ? prev.filter((addonId) => addonId !== addon.id)
                            : [...prev, addon.id]
                        ));
                      }}
                    >
                      <span>{addon.name}</span>
                      <span>+{addon.price} zł · +{addon.duration_minutes} min</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {selectedService && (
            <div className="booking-selection-summary">
              <span>Łączny czas: {pricingSummary.duration} min</span>
              <span>Łączna cena: {pricingSummary.price} zł</span>
            </div>
          )}
          <div className="form-group">
            <label>Wolny termin *</label>
            {slotsLoading && <div className="loading-text">Szukam wolnych terminów...</div>}

            {!slotsLoading && form.service_id && groupedSlots.length === 0 && (
              <div className="empty-text">Brak wolnych terminów w najbliższych dniach.</div>
            )}

            {!slotsLoading && groupedSlots.length > 0 && (
              <div className="slot-groups reschedule-slot-groups">
                {groupedSlots.map(([dayKey, daySlots]) => (
                  <div key={dayKey} className="slot-day-group">
                    <div className="slot-day-label">
                      {format(new Date(`${dayKey}T00:00:00`), 'EEEE, d MMMM', { locale: pl })}
                    </div>
                    <div className="slot-row">
                      {daySlots.map((slot) => {
                        const active = form.scheduled_at === slot.start;
                        return (
                          <button
                            key={slot.start}
                            type="button"
                            className={`slot-btn ${active ? 'slot-btn--selected' : ''}`}
                            onClick={() => {
                              setError('');
                              setFieldErrors((prev) => ({ ...prev, scheduled_at: '' }));
                              setForm((prev) => ({ ...prev, scheduled_at: slot.start }));
                            }}
                          >
                            {format(new Date(slot.start), 'HH:mm')}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {selectedSlotLabel && <div className="slot-selection-summary">Wybrany termin: {selectedSlotLabel}</div>}
            {fieldErrors.scheduled_at && <div className="field-error">{fieldErrors.scheduled_at}</div>}
          </div>
          <div className="form-group">
            <label>Notatka (opcjonalnie)</label>
            <input
              type="text"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="np. alergia na lakier"
            />
          </div>
          <div className="modal-footer">
            <button type="button" className="btn-ghost" onClick={requestClose}>Anuluj</button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Zapisuję...' : 'Dodaj wizytę'}
            </button>
          </div>
        </form>
      </ModalShell>

      <ConfirmDialog
        open={showCloseConfirm}
        title="Zamknąć formularz wizyty?"
        description="Masz niezapisane dane wizyty. Jeśli zamkniesz okno, wpisane informacje przepadną."
        confirmLabel="Zamknij bez zapisu"
        cancelLabel="Wracam do formularza"
        confirmTone="danger"
        onCancel={() => setShowCloseConfirm(false)}
        onConfirm={() => {
          setShowCloseConfirm(false);
          onClose();
        }}
      />
    </>
  );
}
